import { Dropdown, MenuItem } from '@components/dropdown';
import { BtnSecondary } from '@components/buttons';
import {
  IcoAcademicCap,
  IcoArchive,
  IcoCalendar,
  IcoCheck,
  IcoCube,
  IcoDotsVertical,
  IcoDownload,
  IcoEye,
  IcoFolder,
  IcoPencil,
  IcoTag,
} from '@components/icons';
import { categoryLabel, FileThumbnail } from './file-thumbnail';
import dayjs from 'dayjs';
import { timeago } from 'shared/dateutil';
import { formatBytes } from 'shared/formatting';
import { showMediaPreview } from './preview-modal';
import { ManagedFile, View } from './state';
import { splitLiteralMatches } from './highlight-matches';

function downloadUrl(file: ManagedFile) {
  return `${file.url}?download=${encodeURIComponent(file.name)}`;
}

function SizeBadge({ size }: { size: number | undefined }) {
  const formatted = formatBytes(size);
  if (!formatted) {
    return null;
  }
  return (
    <span class="inline-flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
      <IcoCube class="size-3.5 opacity-70" />
      {formatted}
    </span>
  );
}

function UsageBadge({ count, onOpen }: { count: number; onOpen?(): void }) {
  const label =
    count === 0 ? 'No course uses found' : `Used in ${count} course${count === 1 ? '' : 's'}`;
  const styleColor =
    count === 0 ? 'text-gray-500 hover:bg-gray-100' : 'text-indigo-700 hover:bg-indigo-50';
  const iconOpacity = count === 0 ? 'opacity-60' : 'opacity-80';
  const title =
    count === 0
      ? "No course uses found. Images pasted into lessons or comments aren't included in this check."
      : undefined;
  if (!onOpen) {
    return (
      <span
        class={`inline-flex items-center gap-1 text-xs whitespace-nowrap ${
          count === 0 ? 'text-gray-400' : 'text-indigo-700'
        }`}
        title={title}
      >
        <IcoAcademicCap class={`size-3.5 ${iconOpacity}`} />
        {label}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      title={title}
      class={`inline-flex items-center gap-1 text-xs whitespace-nowrap px-2 py-1 rounded-md border border-gray-200 ${styleColor}`}
    >
      <IcoAcademicCap class={`size-3.5 ${iconOpacity}`} />
      {label}
    </button>
  );
}

function SelectCheckbox({
  isSelected,
  onChange,
  label,
  class: className = '',
}: {
  isSelected: boolean;
  onChange(): void;
  label: string;
  class?: string;
}) {
  return (
    <input
      type="checkbox"
      checked={isSelected}
      onClick={(e) => e.stopPropagation()}
      onChange={onChange}
      aria-label={`Select ${label}`}
      class={`focus:ring-indigo-500 text-indigo-600 border-gray-300 rounded-sm cursor-pointer ${className}`}
    />
  );
}

function PreviewButton({ file }: { file: ManagedFile }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void showMediaPreview(file);
      }}
      title="Preview"
      class="inline-flex items-center gap-1 text-xs whitespace-nowrap px-2 py-1 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-100"
    >
      <IcoEye class="size-3.5 opacity-80" />
      Preview
    </button>
  );
}

/**
 * Mobile-only primary action: pick this file and hand it back to whatever opened
 * the picker. Mirrors a row tap, but gives phones an explicit, obvious target.
 */
function UseButton({ onUse }: { onUse(): void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onUse();
      }}
      class="md:hidden inline-flex items-center gap-1 text-xs whitespace-nowrap px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-500"
    >
      <IcoCheck class="size-3.5" />
      Use
    </button>
  );
}

function PickMark({ isSelected, class: className }: { isSelected: boolean; class: string }) {
  return (
    <span
      aria-hidden="true"
      class={`${className} shrink-0 flex items-center justify-center rounded-full border-2 shadow-sm ${
        isSelected
          ? 'border-indigo-600 bg-indigo-600 text-white'
          : 'border-gray-300 bg-white/90 text-transparent'
      }`}
    >
      <IcoCheck class="size-3.5" />
    </span>
  );
}

function formatDate(value: string) {
  return dayjs(value).format('MMM D, YYYY');
}

function HighlightedFilename({ name, searchTerm }: { name: string; searchTerm?: string }) {
  return (
    <>
      {splitLiteralMatches(name, searchTerm).map((part, index) =>
        part.matched ? (
          <mark key={index} class="bg-yellow-100 text-inherit">
            {part.text}
          </mark>
        ) : (
          part.text
        ),
      )}
    </>
  );
}

function DateBadge({
  value,
  label,
  class: className = '',
}: {
  value: string;
  label?: string;
  class?: string;
}) {
  return (
    <span
      class={`inline-flex items-center gap-1 text-xs ${className || 'text-gray-500'}`}
      title={`${label ? `${label} ` : ''}${formatDate(value)}`}
    >
      <IcoCalendar class="size-3.5 opacity-70" />
      {label ? `${label} ` : ''}
      {timeago(value, ' ago')}
    </span>
  );
}

type Props = {
  items: ManagedFile[];
  /** True when viewing the Archived entry — flips Archive→Unarchive + empty copy. */
  archivedView: boolean;
  /** True when a category/name/course filter is narrowing the results. */
  filtered?: boolean;
  /** True when a folder is selected with no other filters active. */
  folderEmpty?: boolean;
  scopeName?: string;
  onClearFilters?(): void;
  view: View;
  searchTerm?: string;
  activeTagId?: string;
  showAddedDate?: boolean;
  showActions: boolean;
  /** When true, render selection checkboxes and let the parent track selection. */
  selectable?: boolean;
  selected?: Set<string>;
  multiSelect?: boolean;
  highlightedFileId?: string;
  onToggleSelect?(id: string): void;
  onClick(file: ManagedFile): void;
  onArchive?(file: ManagedFile): void;
  onUnarchive?(file: ManagedFile): void;
  onRename?(file: ManagedFile): void;
  onMove?(file: ManagedFile): void;
  onEditTags?(file: ManagedFile): void;
};

export function MediaList({
  items,
  archivedView,
  filtered,
  folderEmpty,
  scopeName,
  onClearFilters,
  view,
  searchTerm,
  activeTagId,
  showAddedDate,
  showActions,
  selectable,
  selected,
  multiSelect,
  highlightedFileId,
  onToggleSelect,
  onClick,
  onArchive,
  onUnarchive,
  onRename,
  onMove,
  onEditTags,
}: Props) {
  if (items.length === 0) {
    return (
      <div class="text-gray-500 text-sm border border-dashed border-gray-300 rounded-lg p-8 text-center">
        {filtered ? (
          <>
            <p>
              {scopeName
                ? `No files in ${scopeName} match these filters.`
                : 'No files match these filters.'}
            </p>
            {onClearFilters && (
              <BtnSecondary onClick={onClearFilters} class="mt-3 rounded-full">
                Clear filters
              </BtnSecondary>
            )}
          </>
        ) : archivedView ? (
          'No archived files. Files you archive will show up here.'
        ) : folderEmpty ? (
          'This folder is empty. Move files here to organize them.'
        ) : (
          'No files yet. Upload files to add them to your library.'
        )}
      </div>
    );
  }

  if (view === 'list') {
    return (
      <ul class="divide-y divide-gray-200 border border-gray-200 rounded-lg bg-white">
        {items.map((file) => (
          <MediaRow
            key={file.id}
            file={file}
            archivedView={archivedView}
            showActions={showActions}
            selectable={selectable}
            isSelected={selected?.has(file.id) ?? false}
            multiSelect={multiSelect}
            highlighted={highlightedFileId === file.id}
            searchTerm={searchTerm}
            activeTagId={activeTagId}
            showAddedDate={showAddedDate}
            onToggleSelect={onToggleSelect}
            onClick={onClick}
            onArchive={onArchive}
            onUnarchive={onUnarchive}
            onRename={onRename}
            onMove={onMove}
            onEditTags={onEditTags}
          />
        ))}
      </ul>
    );
  }

  return (
    <ul class="grid grid-cols-[repeat(auto-fill,minmax(min(100%,240px),1fr))] gap-4">
      {items.map((file) => (
        <MediaCard
          key={file.id}
          file={file}
          archivedView={archivedView}
          showActions={showActions}
          selectable={selectable}
          isSelected={selected?.has(file.id) ?? false}
          multiSelect={multiSelect}
          highlighted={highlightedFileId === file.id}
          searchTerm={searchTerm}
          activeTagId={activeTagId}
          showAddedDate={showAddedDate}
          onToggleSelect={onToggleSelect}
          onClick={onClick}
          onArchive={onArchive}
          onUnarchive={onUnarchive}
          onRename={onRename}
          onMove={onMove}
          onEditTags={onEditTags}
        />
      ))}
    </ul>
  );
}

type RowProps = {
  file: ManagedFile;
  archivedView: boolean;
  showActions: boolean;
  selectable?: boolean;
  isSelected: boolean;
  multiSelect?: boolean;
  highlighted?: boolean;
  searchTerm?: string;
  activeTagId?: string;
  showAddedDate?: boolean;
  onToggleSelect?(id: string): void;
  onClick(file: ManagedFile): void;
  onArchive?(file: ManagedFile): void;
  onUnarchive?(file: ManagedFile): void;
  onRename?(file: ManagedFile): void;
  onMove?(file: ManagedFile): void;
  onEditTags?(file: ManagedFile): void;
};

function MediaCard({
  file,
  archivedView,
  showActions,
  selectable,
  isSelected,
  multiSelect,
  highlighted,
  searchTerm,
  activeTagId,
  showAddedDate,
  onToggleSelect,
  onClick,
  onArchive,
  onUnarchive,
  onRename,
  onMove,
  onEditTags,
}: RowProps) {
  return (
    <li
      id={`media-file-${file.id}`}
      class={`relative border rounded-lg bg-white transition-all ${
        highlighted
          ? 'border-indigo-600 ring-2 ring-indigo-400'
          : isSelected
            ? 'border-indigo-500 ring-2 ring-indigo-200'
            : 'border-gray-200'
      }`}
    >
      <button
        type="button"
        onClick={() => onClick(file)}
        aria-pressed={multiSelect ? isSelected : undefined}
        class="group w-full text-left cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 rounded-t-lg block"
      >
        <div class="aspect-square bg-gray-50 group-hover:bg-gray-100 transition-colors rounded-t-lg overflow-hidden">
          <FileThumbnail
            type={file.type}
            name={file.name}
            publicUrl={file.publicUrl}
            posterUrl={file.posterUrl}
            class="w-full h-full"
            iconClass="size-10"
            showPlayOverlay
          />
        </div>
        <div class="px-3 py-2.5 border-t border-gray-200 min-w-0 flex flex-col gap-1.5 group-hover:bg-gray-50 transition-colors">
          <div class="truncate text-sm font-medium text-gray-800" title={file.name}>
            <HighlightedFilename name={file.name} searchTerm={searchTerm} />
          </div>
          <div class="flex items-center gap-2 text-xs text-gray-500 min-w-0">
            <span class="truncate">{categoryLabel(file.type)}</span>
            <SizeBadge size={file.size} />
            <DateBadge
              value={showAddedDate ? file.createdAt : file.lastImportedAt}
              label={showAddedDate ? 'Added' : 'Used'}
              class="whitespace-nowrap ml-auto shrink-0"
            />
          </div>
          {file.tags.length > 0 && <FileTags tags={file.tags} activeTagId={activeTagId} />}
        </div>
      </button>
      {selectable && onToggleSelect && (
        <SelectCheckbox
          isSelected={isSelected}
          onChange={() => onToggleSelect(file.id)}
          label={file.name}
          class="absolute top-2 left-2 size-5 shadow-sm"
        />
      )}
      {multiSelect && (
        <PickMark
          isSelected={isSelected}
          class="pointer-events-none absolute top-2 left-2 size-6"
        />
      )}
      <div
        class="px-3 py-2 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <UsageBadge
          count={file.usageCourseCount}
          onOpen={showActions ? () => onClick(file) : undefined}
        />
        <div class="flex items-center gap-1">
          {!showActions && <PreviewButton file={file} />}
          {showActions && (
            <RowActions
              file={file}
              archivedView={archivedView}
              onArchive={onArchive}
              onUnarchive={onUnarchive}
              onRename={onRename}
              onMove={onMove}
              onEditTags={onEditTags}
              triggerClass="p-1.5 rounded-md text-gray-500 hover:bg-gray-100"
            />
          )}
        </div>
      </div>
    </li>
  );
}

function MediaRow({
  file,
  archivedView,
  showActions,
  selectable,
  isSelected,
  multiSelect,
  highlighted,
  searchTerm,
  activeTagId,
  showAddedDate,
  onToggleSelect,
  onClick,
  onArchive,
  onUnarchive,
  onRename,
  onMove,
  onEditTags,
}: RowProps) {
  return (
    <li
      id={`media-file-${file.id}`}
      class={`flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-2 px-4 py-3 first:rounded-t-lg last:rounded-b-lg ${
        highlighted
          ? 'bg-indigo-100 ring-2 ring-inset ring-indigo-400'
          : isSelected
            ? 'bg-indigo-50'
            : 'hover:bg-gray-50'
      }`}
    >
      {selectable && onToggleSelect && (
        <SelectCheckbox
          isSelected={isSelected}
          onChange={() => onToggleSelect(file.id)}
          label={file.name}
          class="size-4"
        />
      )}
      <button
        type="button"
        onClick={() => onClick(file)}
        aria-pressed={multiSelect ? isSelected : undefined}
        class="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"
      >
        {multiSelect && <PickMark isSelected={isSelected} class="size-5" />}
        <FileThumbnail
          type={file.type}
          name={file.name}
          publicUrl={file.publicUrl}
          posterUrl={file.posterUrl}
          class="size-12 rounded-md"
          showPlayOverlay
        />
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm font-medium text-gray-900" title={file.name}>
            <HighlightedFilename name={file.name} searchTerm={searchTerm} />
          </div>
          <div class="flex items-center gap-2 text-xs text-gray-500 mt-0.5 flex-wrap">
            <span class="truncate">{categoryLabel(file.type)}</span>
            <SizeBadge size={file.size} />
          </div>
          {file.tags.length > 0 && <FileTags tags={file.tags} activeTagId={activeTagId} />}
        </div>
        <div class="hidden md:flex justify-start w-28">
          <DateBadge
            value={showAddedDate ? file.createdAt : file.lastImportedAt}
            label={showAddedDate ? 'Added' : 'Used'}
            class="whitespace-nowrap"
          />
        </div>
      </button>
      <div
        class={`flex items-center gap-1.5 ${showActions ? 'shrink-0' : 'basis-full justify-end md:basis-auto md:justify-normal'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div class="hidden md:flex md:justify-start shrink-0">
          <UsageBadge
            count={file.usageCourseCount}
            onOpen={showActions ? () => onClick(file) : undefined}
          />
        </div>
        {!showActions && <PreviewButton file={file} />}
        {!showActions && !multiSelect && <UseButton onUse={() => onClick(file)} />}
        {showActions && (
          <RowActions
            file={file}
            archivedView={archivedView}
            onArchive={onArchive}
            onUnarchive={onUnarchive}
            onRename={onRename}
            onMove={onMove}
            onEditTags={onEditTags}
            triggerClass="p-2 rounded-md hover:bg-gray-100"
          />
        )}
      </div>
    </li>
  );
}

function RowActions({
  file,
  archivedView,
  onArchive,
  onUnarchive,
  onRename,
  onMove,
  onEditTags,
  triggerClass,
}: {
  file: ManagedFile;
  archivedView: boolean;
  onArchive?(file: ManagedFile): void;
  onUnarchive?(file: ManagedFile): void;
  onRename?(file: ManagedFile): void;
  onMove?(file: ManagedFile): void;
  onEditTags?(file: ManagedFile): void;
  triggerClass: string;
}) {
  return (
    <Dropdown
      hideDownIcon
      triggerClass={triggerClass}
      renderMenu={() => (
        <>
          <MenuItem regularCase href={downloadUrl(file)}>
            <span class="inline-flex items-center gap-2">
              <IcoDownload class="size-4" /> Download
            </span>
          </MenuItem>
          {onRename && (
            <MenuItem regularCase onClick={() => onRename(file)}>
              <span class="inline-flex items-center gap-2">
                <IcoPencil class="size-4" /> Rename
              </span>
            </MenuItem>
          )}
          {onMove && (
            <MenuItem regularCase onClick={() => onMove(file)}>
              <span class="inline-flex items-center gap-2">
                <IcoFolder class="size-4" /> Move to folder…
              </span>
            </MenuItem>
          )}
          {onEditTags && (
            <MenuItem regularCase onClick={() => onEditTags(file)}>
              <span class="inline-flex items-center gap-2">
                <IcoTag class="size-4" /> Edit tags…
              </span>
            </MenuItem>
          )}
          {!archivedView && onArchive && (
            <MenuItem regularCase onClick={() => onArchive(file)}>
              <span class="inline-flex items-center gap-2">
                <IcoArchive class="size-4" /> Archive
              </span>
            </MenuItem>
          )}
          {archivedView && onUnarchive && (
            <MenuItem regularCase onClick={() => onUnarchive(file)}>
              <span class="inline-flex items-center gap-2">
                <IcoArchive class="size-4" /> Unarchive
              </span>
            </MenuItem>
          )}
        </>
      )}
    >
      <span class="sr-only">Actions for {file.name}</span>
      <IcoDotsVertical />
    </Dropdown>
  );
}

const VISIBLE_TAG_COUNT = 3;

function FileTags({ tags, activeTagId }: { tags: ManagedFile['tags']; activeTagId?: string }) {
  const ordered = activeTagId
    ? [...tags].sort((a, b) => Number(b.id === activeTagId) - Number(a.id === activeTagId))
    : tags;
  const hidden = ordered.slice(VISIBLE_TAG_COUNT);
  return (
    <span class="flex min-w-0 flex-wrap gap-1">
      {ordered.slice(0, VISIBLE_TAG_COUNT).map((tag) => (
        <span
          key={tag.id}
          class="max-w-full truncate rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
          title={tag.name}
        >
          {tag.name}
        </span>
      ))}
      {hidden.length > 0 && (
        <span
          class="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
          title={hidden.map((tag) => tag.name).join(', ')}
        >
          <span aria-hidden="true">+{hidden.length}</span>
          <span class="sr-only">
            {`, ${hidden.length} more tag${hidden.length === 1 ? '' : 's'}: ${hidden.map((tag) => tag.name).join(', ')}`}
          </span>
        </span>
      )}
    </span>
  );
}
